import React, { useId } from "react"
import { theme } from "../../settings/config"
import {
	COURT,
	FIELD,
	FIELD_RADIUS,
	SURFACE,
	SURFACE_RADIUS,
	arrowPoint,
	schemaView,
} from "../../utils/exerciseSchema.utils"

// Il disegno di uno schema (src/utils/exerciseSchema.utils.js), in SVG: il
// campo blu sul verde, le zone, le frecce e i pezzi, come nelle schede del
// maestro. I pezzi qui sotto li usa anche l'editor, che ci aggiunge sopra
// quello che serve al dito.

const c = theme.colors
const fmt = n => n.toFixed(1)

// Una riga del campo, da un punto a un altro in metri
const Line = ({ view, from, to, ...rest }) => {
	const a = view.toScreen(...from)
	const b = view.toScreen(...to)
	return <line x1={fmt(a.X)} y1={fmt(a.Y)} x2={fmt(b.X)} y2={fmt(b.Y)} {...rest} />
}

export const polygonPoints = (view, corners) =>
	corners
		.map(([x, y]) => {
			const p = view.toScreen(x, y)
			return `${fmt(p.X)},${fmt(p.Y)}`
		})
		.join(" ")

// Un rettangolo coi lati a `hx` e `hy` dal centro e gli angoli tondi, a
// pezzetti: in prospettiva un arco del campo non è un arco dello schermo
const roundedRect = (hx, hy, r) =>
	[
		[hx - r, -hy + r, -90],
		[hx - r, hy - r, 0],
		[-hx + r, hy - r, 90],
		[-hx + r, -hy + r, 180],
	]
		.flatMap(([cx, cy, start]) =>
			Array.from({ length: 11 }, (_, i) => {
				const angle = ((start + i * 9) * Math.PI) / 180
				return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)]
			}),
		)

// `grass`: la cornice intorno al fondo. Dove il campo sta già sul suo
// colore, fino ai bordi (l'editor, la voce del quaderno, i campi piccoli del
// profilo e del circolo), non serve.
export function SchemaCourt({ view, grass = true }) {
	const meshID = useId().replace(/:/g, "")
	const w = FIELD.width / 2
	const l = FIELD.length / 2
	// il blu va ben oltre le righe, come su un campo vero
	const bx = SURFACE.x
	const by = SURFACE.y
	const { doubles, singles, baseline, service, post } = COURT

	// la rete: in prospettiva è una fascia in piedi, dall'alto una riga
	const left = view.toScreen(-post, 0)
	const right = view.toScreen(post, 0)
	const netHeight = 15 * left.k

	return (
		<g>
			{grass && <polygon points={polygonPoints(view, roundedRect(w, l, FIELD_RADIUS))} fill={c.schema_grass} />}
			<polygon points={polygonPoints(view, roundedRect(bx, by, SURFACE_RADIUS))} fill={c.schema_court} />
			{/* dentro le righe del doppio: sul cemento è blu nel verde, come agli
			    US Open; sulla terra e sull'erba è dello stesso colore del resto */}
			<polygon
				points={polygonPoints(view, [
					[-doubles, -baseline],
					[doubles, -baseline],
					[doubles, baseline],
					[-doubles, baseline],
				])}
				fill={c.schema_inside}
			/>
			<g stroke={c.schema_line} strokeWidth="1.6" strokeLinecap="square">
				{[-baseline, baseline].map(y => (
					<Line key={`b${y}`} view={view} from={[-doubles, y]} to={[doubles, y]} />
				))}
				{[-doubles, doubles, -singles, singles].map(x => (
					<Line key={`s${x}`} view={view} from={[x, -baseline]} to={[x, baseline]} />
				))}
				{[-service, service].map(y => (
					<Line key={`v${y}`} view={view} from={[-singles, y]} to={[singles, y]} />
				))}
				<Line view={view} from={[0, -service]} to={[0, service]} />
				<Line view={view} from={[0, -baseline]} to={[0, -baseline + 0.35]} />
				<Line view={view} from={[0, baseline]} to={[0, baseline - 0.35]} />
			</g>
			{view.mode === "persp" ? (
				<g>
					<defs>
						<pattern id={meshID} width="3" height="3" patternUnits="userSpaceOnUse">
							<path d="M0,0 L3,3 M3,0 L0,3" stroke={c.schema_mesh} strokeWidth="0.6" />
						</pattern>
					</defs>
					<rect
						x={fmt(left.X)}
						y={fmt(left.Y - netHeight)}
						width={fmt(right.X - left.X)}
						height={fmt(netHeight)}
						fill={`url(#${meshID})`}
					/>
					<line x1={fmt(left.X)} y1={fmt(left.Y - netHeight)} x2={fmt(right.X)} y2={fmt(right.Y - netHeight)} stroke={c.schema_line} strokeWidth="1.4" />
					<line x1={fmt(left.X)} y1={fmt(left.Y)} x2={fmt(right.X)} y2={fmt(right.Y)} stroke={c.schema_net} strokeWidth="1" />
					{[left, right].map((p, i) => (
						<rect key={i} x={fmt(p.X - 1.6)} y={fmt(p.Y - netHeight - 2)} width="3.2" height={fmt(netHeight + 3)} rx="1" fill={c.schema_net} />
					))}
				</g>
			) : (
				<g>
					<line x1={fmt(left.X)} y1={fmt(left.Y)} x2={fmt(right.X)} y2={fmt(right.Y)} stroke={c.schema_net} strokeWidth="3.2" />
					<line x1={fmt(left.X)} y1={fmt(left.Y)} x2={fmt(right.X)} y2={fmt(right.Y)} stroke={c.schema_line} strokeWidth="1" strokeDasharray="2 2" />
					{[left, right].map((p, i) => (
						<circle key={i} cx={fmt(p.X)} cy={fmt(p.Y)} r="2.6" fill={c.schema_net} />
					))}
				</g>
			)}
		</g>
	)
}

// Il numero nel tondo nero, quello delle schede
export const Badge = ({ n, x, y }) => (
	<g>
		<circle cx={x} cy={y} r="7.5" fill={c.schema_badge} stroke={c.schema_line} strokeWidth="1.4" />
		<text x={x} y={y + 3.4} textAnchor="middle" fontSize="10" fontWeight="700" fill={c.schema_line} fontFamily={theme.fontFamily.primary}>
			{n}
		</text>
	</g>
)

// Un pezzo coi piedi nel punto (0,0): la sagoma, il maestro, un cono o una
// coppella, o un numero da solo
export function PieceGlyph({ item }) {
	if (item.type === "player") {
		const fill = c[`schema_${item.color}`]
		return (
			<g>
				<g opacity={item.ghost ? 0.45 : 1}>
					<ellipse cx="0" cy="0" rx="13" ry="4" fill={c.schema_shadow} />
					<path d="M-12,-1 C-11,-6 -8,-14 -5,-17 L5,-17 C8,-14 11,-6 12,-1 C6,2 -6,2 -12,-1Z" fill={fill} />
					<path d="M0,-17 L5,-17 C8,-14 11,-6 12,-1 C8,1 4,1.6 0,1.7Z" fill={c.schema_shade} />
					<circle cx="0" cy="-22" r="6.5" fill={fill} />
					<circle cx="-2.2" cy="-24.2" r="2.2" fill={c.schema_shine} />
				</g>
				{item.num > 0 && <Badge n={item.num} x={11} y={-4} />}
			</g>
		)
	}
	if (item.type === "coach")
		return (
			<g>
				<circle cx="0" cy="-10" r="10" fill={c.schema_coach} stroke={c.schema_line} strokeWidth="1.2" />
				<text x="0" y="-5.4" textAnchor="middle" fontSize="14" fontWeight="700" fill={c.schema_line} fontFamily="Georgia, serif">
					i
				</text>
			</g>
		)
	// il cono alto, arancione con la fascia bianca
	if (item.type === "cone" && item.tall)
		return (
			<g>
				<ellipse cx="0" cy="0" rx="9" ry="3.2" fill={c.schema_shadow} />
				<path d="M-8.5,-1 L8.5,-1 L7.5,1.6 L-7.5,1.6Z" fill={c.schema_cone_dark} />
				<path d="M-6.5,-1 L-1.6,-24 L1.6,-24 L6.5,-1Z" fill={c.schema_cone} />
				<path d="M-4.2,-11.5 L-3.3,-15.5 L3.3,-15.5 L4.2,-11.5Z" fill={c.schema_line} />
				<path d="M0,-24 L1.6,-24 L6.5,-1 L0,-1Z" fill={c.schema_shade} />
			</g>
		)
	// la coppella, il cinesino piatto
	if (item.type === "cone")
		return (
			<g>
				<ellipse cx="0" cy="0" rx="12" ry="4.4" fill={c.schema_cone_dark} />
				<path d="M-12,0 C-9,-7.5 9,-7.5 12,0 C6,2.4 -6,2.4 -12,0Z" fill={c.schema_cone} />
				<ellipse cx="0" cy="-5.2" rx="3" ry="1.1" fill={c.schema_cone_dark} />
			</g>
		)
	return <Badge n={item.n} x={0} y={-8} />
}

// Quanto sta in alto il centro di un pezzo rispetto ai piedi: lì va
// l'anello della selezione, e lì lo si prende col dito
const PIECE_LIFT = { player: 14, coach: 10, cone: 3, number: 8 }
export const pieceLift = item => (item.type === "cone" && item.tall ? 12 : PIECE_LIFT[item.type])

// Quanto si disegna grande un pezzo: in prospettiva un po' di più, per
// stare al passo delle schede, e sempre più piccolo man mano che si va in
// fondo
export const pieceScale = (view, k) => k * (view.mode === "top" ? 1 : 1.25)

export function SchemaPiece({ item, view, children }) {
	const p = view.toScreen(item.x, item.y)
	return (
		<g transform={`translate(${fmt(p.X)},${fmt(p.Y)}) scale(${pieceScale(view, p.k).toFixed(3)})`}>
			<PieceGlyph item={item} />
			{children}
		</g>
	)
}

export const zoneCorners = item => [
	[item.x1, item.y1],
	[item.x2, item.y1],
	[item.x2, item.y2],
	[item.x1, item.y2],
]

export const SchemaZone = ({ item, view, ...rest }) => (
	<polygon
		points={polygonPoints(view, zoneCorners(item))}
		fill={c.schema_zone}
		stroke={c.schema_zone_line}
		strokeWidth="1.4"
		{...rest}
	/>
)

// La curva di una freccia sullo schermo, a pezzetti: in prospettiva una
// curva del campo non è una curva dello schermo, e così si piega giusta
const SAMPLES = 24
export const arrowScreenPoints = (item, view) =>
	Array.from({ length: SAMPLES + 1 }, (_, i) => {
		const p = arrowPoint(item, i / SAMPLES)
		return view.toScreen(p.x, p.y)
	})

export const pathOf = points => "M" + points.map(p => `${fmt(p.X)},${fmt(p.Y)}`).join(" L")

// La punta in `to`, che arriva da `from`
const arrowHead = (from, to, size) => {
	const dx = to.X - from.X
	const dy = to.Y - from.Y
	const length = Math.hypot(dx, dy) || 1
	const ux = dx / length
	const uy = dy / length
	const bx = to.X - ux * size
	const by = to.Y - uy * size
	const w = size * 0.62
	return `${fmt(to.X)},${fmt(to.Y)} ${fmt(bx - uy * w)},${fmt(by + ux * w)} ${fmt(bx + uy * w)},${fmt(by - ux * w)}`
}

// La riga si ferma dove comincia la punta, se no il tratteggio ci spunta
const trimEnd = (points, atEnd, cut) => {
	const out = points.slice()
	const i = atEnd ? out.length - 1 : 0
	const j = atEnd ? out.length - 2 : 1
	const dx = out[i].X - out[j].X
	const dy = out[i].Y - out[j].Y
	const length = Math.hypot(dx, dy) || 1
	const step = Math.min(cut, length * 0.9)
	out[i] = { ...out[i], X: out[i].X - (dx / length) * step, Y: out[i].Y - (dy / length) * step }
	return out
}

// La palla tratteggiata, lo spostamento pieno; tutti e due gialli, con la
// punta (due, se è la doppia freccia)
export function SchemaArrow({ item, view }) {
	const points = arrowScreenPoints(item, view)
	const last = points.length - 1
	const endSize = 12 * points[last].k
	const startSize = 12 * points[0].k
	const both = item.both
	let line = trimEnd(points, true, endSize * 0.8)
	if (both) line = trimEnd(line, false, startSize * 0.8)
	return (
		<g>
			<path
				d={pathOf(line)}
				fill="none"
				stroke={c.schema_ball}
				strokeWidth="2.6"
				strokeLinecap="round"
				strokeDasharray={item.kind === "palla" ? "6 4.5" : undefined}
			/>
			<polygon points={arrowHead(points[last - 2], points[last], endSize)} fill={c.schema_ball} />
			{both && <polygon points={arrowHead(points[2], points[0], startSize)} fill={c.schema_ball} />}
		</g>
	)
}

// I pezzi dal fondo in avanti: chi è più vicino copre chi è dietro
export const piecesByDepth = (items, view) =>
	items
		.map((item, index) => ({ item, index }))
		.filter(({ item }) => item.type !== "zone" && item.type !== "arrow")
		.sort((a, b) => view.toScreen(a.item.x, a.item.y).Y - view.toScreen(b.item.x, b.item.y).Y)

// Lo schema intero, da guardare: nelle miniature e grande. In prospettiva,
// se non si chiede altro, come le schede.
function SchemaDrawing({ schema, mode = "persp", label = "Schema dell'esercizio", grass = true, style }) {
	const view = schemaView(mode)
	const items = schema?.items ?? []
	return (
		<svg
			viewBox={`0 0 ${view.width} ${fmt(view.height)}`}
			role="img"
			aria-label={label}
			style={{ display: "block", width: "100%", height: "100%", ...style }}>
			<SchemaCourt view={view} grass={grass} />
			{items.map((item, i) => item.type === "zone" && <SchemaZone key={i} item={item} view={view} />)}
			{items.map((item, i) => item.type === "arrow" && <SchemaArrow key={i} item={item} view={view} />)}
			{piecesByDepth(items, view).map(({ item, index }) => (
				<SchemaPiece key={index} item={item} view={view} />
			))}
		</svg>
	)
}

export default SchemaDrawing
